import React, { useEffect, useMemo, useState } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtCHFprice } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Crown, Building2, Shield, CreditCard, Smartphone, Loader2 } from "lucide-react";
import { useSeoHead } from "../lib/seoHead";
import { getPageSeo, buildHreflangs, buildBreadcrumbList } from "../lib/pageSeo";
import JsonLd from "../components/JsonLd";
import { useActiveLocale } from "../lib/i18n";
import { trackEvent } from "../lib/analytics";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");

// Phase 56 — Locale-aware copy. Keep tightly scoped to this page to avoid a
// migration of the central LanguageContext for a single screen.
const COPY = {
  de: {
    kicker: "Schweizer Preise · CHF · MwSt. inkl.",
    title: "Verkaufe schneller — provisionsfrei.",
    sub: "Klare Schweizer Preise. TWINT oder Karte. Sofort aktiv nach der Zahlung.",
    individuals: "Für Privatkäufer & Verkäufer",
    dealers: "Für Händler & Garagen",
    free: "Kostenlos",
    perMonth: "/ Monat",
    once: "einmalig",
    days: "Tage",
    listings: "aktive Inserate",
    select: "Auswählen",
    activate: "Aktivieren",
    processing: "Wird verarbeitet…",
    current: "Aktiv",
    mostPopular: "Beliebteste",
    bestForDealers: "Empfohlen",
    trialBadge: "14 Tage gratis testen",
    trialNote: "14 Tage gratis · jederzeit kündbar · Kreditkarte erforderlich",
    secure: "Sichere Zahlung",
    methods: "TWINT, Visa, Mastercard, American Express, Apple Pay, Google Pay",
    methodsDealer: "Kreditkarte (TWINT bei Abos nicht möglich)",
    note: "Alle Preise in CHF inkl. MwSt. Inserate werden nach Ablauf automatisch deaktiviert. Dealer-Abos monatlich kündbar.",
    needHelp: "Brauchst du Hilfe bei der Auswahl?",
    contactCta: "Kontakt aufnehmen →",
    plans: {
      free: { name: "Free", tagline: "Privates Inserieren — gratis ausprobieren.", features: ["1 Inserat", "7 Tage Laufzeit", "Bis zu 12 Fotos", "Standard-Sichtbarkeit"] },
      boost: { name: "Boost", tagline: "7 Tage zuoberst in den Suchresultaten.", features: ["1 Inserat", "Boost-Badge", "7 Tage Top-Platzierung", "TWINT oder Karte"] },
      premium: { name: "Premium", tagline: "Hervorgehobene Darstellung & Priority Placement.", features: ["1 Inserat", "Premium-Badge", "30 Tage Priority Placement", "Bis zu 20 Fotos", "TWINT oder Karte"] },
      dealer_basic: { name: "Basic Dealer", tagline: "Für kleine Garagen mit bis zu 20 Fahrzeugen.", features: ["20 aktive Inserate", "Dealer-Badge", "Eigenes Händlerprofil", "Basis-Analytics", "Monatlich kündbar"] },
      dealer_pro: { name: "Pro Dealer", tagline: "Für etablierte Händler mit grossem Bestand.", features: ["100 aktive Inserate", "Premium-Dealer-Badge", "Priority Placement", "Erweiterte Analytics", "Vorrang im Support", "Monatlich kündbar"] },
    },
  },
  fr: {
    kicker: "Prix suisses · CHF · TVA incl.",
    title: "Vendez plus vite — sans commission.",
    sub: "Prix suisses transparents. TWINT ou carte. Actif immédiatement après paiement.",
    individuals: "Pour particuliers",
    dealers: "Pour concessionnaires & garages",
    free: "Gratuit",
    perMonth: "/ mois",
    once: "paiement unique",
    days: "jours",
    listings: "annonces actives",
    select: "Choisir",
    activate: "Activer",
    processing: "Traitement…",
    current: "Actif",
    mostPopular: "Le plus populaire",
    bestForDealers: "Recommandé",
    trialBadge: "14 jours d'essai gratuit",
    trialNote: "14 jours gratuits · résiliable à tout moment · carte requise",
    secure: "Paiement sécurisé",
    methods: "TWINT, Visa, Mastercard, Amex, Apple Pay, Google Pay",
    methodsDealer: "Carte de crédit (TWINT non disponible pour les abonnements)",
    note: "Tous les prix en CHF, TVA incluse. Les annonces sont automatiquement désactivées à l'expiration. Abonnements résiliables mensuellement.",
    needHelp: "Besoin d'aide pour choisir?",
    contactCta: "Nous contacter →",
    plans: {
      free: { name: "Free", tagline: "Annonce privée — essai gratuit.", features: ["1 annonce", "7 jours", "Jusqu'à 12 photos", "Visibilité standard"] },
      boost: { name: "Boost", tagline: "7 jours en tête des résultats.", features: ["1 annonce", "Badge Boost", "7 jours en haut de liste", "TWINT ou carte"] },
      premium: { name: "Premium", tagline: "Mise en avant & placement prioritaire.", features: ["1 annonce", "Badge Premium", "30 jours en priorité", "Jusqu'à 20 photos", "TWINT ou carte"] },
      dealer_basic: { name: "Basic Dealer", tagline: "Pour les petits garages, jusqu'à 20 véhicules.", features: ["20 annonces actives", "Badge concessionnaire", "Profil dédié", "Analytics de base", "Résiliable mensuellement"] },
      dealer_pro: { name: "Pro Dealer", tagline: "Pour les concessionnaires établis.", features: ["100 annonces actives", "Badge Premium", "Placement prioritaire", "Analytics avancés", "Support prioritaire", "Résiliable mensuellement"] },
    },
  },
  it: {
    kicker: "Prezzi svizzeri · CHF · IVA incl.",
    title: "Vendi più velocemente — senza commissioni.",
    sub: "Prezzi svizzeri trasparenti. TWINT o carta. Attivo subito dopo il pagamento.",
    individuals: "Per privati",
    dealers: "Per concessionari",
    free: "Gratis",
    perMonth: "/ mese",
    once: "pagamento unico",
    days: "giorni",
    listings: "annunci attivi",
    select: "Scegli",
    activate: "Attiva",
    processing: "Elaborazione…",
    current: "Attivo",
    mostPopular: "Il più popolare",
    bestForDealers: "Consigliato",
    trialBadge: "14 giorni di prova gratuita",
    trialNote: "14 giorni gratis · cancellabile in qualsiasi momento · carta richiesta",
    secure: "Pagamento sicuro",
    methods: "TWINT, Visa, Mastercard, Amex, Apple Pay, Google Pay",
    methodsDealer: "Carta di credito (TWINT non disponibile per gli abbonamenti)",
    note: "Tutti i prezzi in CHF, IVA inclusa. Gli annunci vengono disattivati automaticamente alla scadenza. Abbonamenti rescindibili mensilmente.",
    needHelp: "Hai bisogno di aiuto per scegliere?",
    contactCta: "Contattaci →",
    plans: {
      free: { name: "Free", tagline: "Annuncio privato — prova gratuita.", features: ["1 annuncio", "7 giorni", "Fino a 12 foto", "Visibilità standard"] },
      boost: { name: "Boost", tagline: "7 giorni in cima ai risultati.", features: ["1 annuncio", "Badge Boost", "7 giorni in primo piano", "TWINT o carta"] },
      premium: { name: "Premium", tagline: "Annuncio in evidenza & placement prioritario.", features: ["1 annuncio", "Badge Premium", "30 giorni in priorità", "Fino a 20 foto", "TWINT o carta"] },
      dealer_basic: { name: "Basic Dealer", tagline: "Per piccoli concessionari, fino a 20 veicoli.", features: ["20 annunci attivi", "Badge concessionario", "Profilo dedicato", "Analytics di base", "Rescindibile mensilmente"] },
      dealer_pro: { name: "Pro Dealer", tagline: "Per concessionari affermati.", features: ["100 annunci attivi", "Badge Premium", "Placement prioritario", "Analytics avanzati", "Supporto prioritario", "Rescindibile mensilmente"] },
    },
  },
  en: {
    kicker: "Swiss pricing · CHF · VAT incl.",
    title: "Sell faster — commission-free.",
    sub: "Transparent Swiss pricing. TWINT or card. Active instantly after payment.",
    individuals: "For private sellers",
    dealers: "For dealers & garages",
    free: "Free",
    perMonth: "/ month",
    once: "one-time",
    days: "days",
    listings: "active listings",
    select: "Choose",
    activate: "Activate",
    processing: "Processing…",
    current: "Active",
    mostPopular: "Most popular",
    bestForDealers: "Recommended",
    trialBadge: "14-day free trial",
    trialNote: "14 days free · cancel anytime · credit card required",
    secure: "Secure payment",
    methods: "TWINT, Visa, Mastercard, Amex, Apple Pay, Google Pay",
    methodsDealer: "Credit card (TWINT not available for subscriptions)",
    note: "All prices in CHF incl. VAT. Listings are automatically deactivated after expiry. Dealer subscriptions cancellable monthly.",
    needHelp: "Need help choosing?",
    contactCta: "Contact us →",
    plans: {
      free: { name: "Free", tagline: "Private listing — try for free.", features: ["1 listing", "7-day duration", "Up to 12 photos", "Standard visibility"] },
      boost: { name: "Boost", tagline: "7 days at the top of search results.", features: ["1 listing", "Boost badge", "7-day top placement", "TWINT or card"] },
      premium: { name: "Premium", tagline: "Highlighted display & priority placement.", features: ["1 listing", "Premium badge", "30-day priority placement", "Up to 20 photos", "TWINT or card"] },
      dealer_basic: { name: "Basic Dealer", tagline: "For small garages with up to 20 vehicles.", features: ["20 active listings", "Dealer badge", "Dedicated profile", "Basic analytics", "Cancel monthly"] },
      dealer_pro: { name: "Pro Dealer", tagline: "For established dealers with large inventory.", features: ["100 active listings", "Premium dealer badge", "Priority placement", "Advanced analytics", "Priority support", "Cancel monthly"] },
    },
  },
};

function PaymentMethodChips({ twint }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="payment-method-chips">
      {twint && (
        <span
          data-testid="pm-twint"
          className="inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-600/15 px-2 py-0.5 text-[10px] font-semibold text-violet-300"
        >
          <Smartphone className="h-3 w-3" /> TWINT
        </span>
      )}
      <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400">
        <CreditCard className="h-3 w-3" /> Card
      </span>
    </div>
  );
}

function PlanCard({ plan, busy, onAction, ctaLabel, highlight, badge, accent = "red" }) {
  const accentMap = {
    red: { border: "border-red-600", bg: "bg-red-600/5", btn: "bg-red-600 hover:bg-red-500 text-white", badge: "bg-red-600" },
    violet: { border: "border-violet-500", bg: "bg-violet-600/5", btn: "bg-violet-600 hover:bg-violet-500 text-white", badge: "bg-violet-600" },
    amber: { border: "border-amber-500", bg: "bg-amber-500/5", btn: "bg-amber-500 hover:bg-amber-400 text-black", badge: "bg-amber-500" },
  };
  const a = accentMap[accent] || accentMap.red;
  return (
    <div
      data-testid={`plan-${plan.id}`}
      className={`relative flex flex-col rounded-2xl border p-6 transition-all ${highlight ? `${a.border} ${a.bg}` : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-700"}`}
    >
      {badge && (
        <Badge data-testid={`plan-badge-${plan.id}`} className={`absolute -top-3 left-6 ${a.badge} text-white hover:${a.badge}`}>
          <Sparkles className="mr-1 h-3 w-3" /> {badge}
        </Badge>
      )}
      <div className="flex items-center gap-2.5">
        {plan.icon}
        <h3 className="font-display text-xl font-bold text-white">{plan.name}</h3>
      </div>
      <p className="mt-2 text-sm text-zinc-400" data-testid={`plan-tagline-${plan.id}`}>{plan.tagline}</p>
      {plan.trialBadge && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-300"
          data-testid={`plan-trial-${plan.id}`}
        >
          <Sparkles className="h-3 w-3" /> {plan.trialBadge}
        </div>
      )}
      <div className="mt-5 flex items-baseline gap-1.5">
        {plan.priceLabel === null ? (
          <span className="font-display text-4xl font-bold text-white">{plan.freeLabel}</span>
        ) : (
          <>
            <span className="font-display text-4xl font-bold text-white" data-testid={`plan-price-${plan.id}`}>
              {plan.priceLabel}
            </span>
            <span className="text-xs text-zinc-500">{plan.priceSuffix}</span>
          </>
        )}
      </div>
      <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" /> <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.twint !== undefined && <PaymentMethodChips twint={plan.twint} />}
      <Button
        onClick={onAction}
        disabled={busy}
        className={`mt-7 w-full ${a.btn}`}
        data-testid={`plan-cta-${plan.id}`}
      >
        {ctaLabel}
      </Button>
    </div>
  );
}

export default function Pricing() {
  const { lang } = useLang();
  const locale = useActiveLocale();
  const c = COPY[lang] || COPY.de;
  const { user } = useAuth();
  const navigate = useLocaleNavigate();
  const [packages, setPackages] = useState([]);
  const [sub, setSub] = useState(null);
  const [usedFree, setUsedFree] = useState(false);
  const [busy, setBusy] = useState("");

  // Phase 63 — SEO head + breadcrumbs
  const seo = getPageSeo("pricing", locale);
  useSeoHead({
    title: seo.title,
    description: seo.description,
    canonical: `${SITE_URL}/${locale}/pricing`,
    image: `${SITE_URL}/icons/og-default.png`,
    hreflangs: buildHreflangs(SITE_URL, "/pricing"),
  });
  const breadcrumbs = buildBreadcrumbList([
    { name: "CarHive", url: `${SITE_URL}/${locale}` },
    { name: seo.title.split(" |")[0], url: `${SITE_URL}/${locale}/pricing` },
  ]);

  useEffect(() => {
    api.packages().then(setPackages);
    if (user) {
      api.mySubscription().then((r) => {
        setSub(r.active);
        setUsedFree(r.used_free);
      });
    }
  }, [user]);

  // Find packages from the catalog response.
  const findById = (id) => packages.find((p) => p.id === id);

  const freePkg = findById("free");
  const boostPkg = findById("boost_7d");
  const premiumPkg = findById("premium_30d");
  const dealerBasic = findById("dealer_basic_monthly");
  const dealerPro = findById("dealer_pro_monthly");

  // Phase 56 — Lightweight analytics. Fire-and-forget; never block UI.
  // Phase 64 — bridge to GA4 via trackEvent in addition to Plausible.
  const track = (event, props = {}) => {
    try {
      trackEvent(event, props);
      if (typeof window === "undefined") return;
      if (typeof window.plausible === "function") window.plausible(event, { props });
      // eslint-disable-next-line no-console
      console.log(`[analytics] ${event}`, props);
    } catch (e) { /* noop */ }
  };

  const activateFree = async () => {
    if (!user) { navigate("/login?next=/pricing"); return; }
    setBusy("free");
    try {
      track("payment_started", { plan: "free", price: 0 });
      await api.activateFree();
      track("payment_completed", { plan: "free", price: 0, method: "none" });
      toast.success("Free Inserat aktiviert");
      navigate("/sell");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setBusy(""); }
  };

  const goToSellWith = (cta) => {
    if (!user) { navigate("/login?next=/pricing"); return; }
    track("payment_started", { plan: cta, price: cta === "boost" ? 9.90 : 19.90 });
    // Phase 56.1 hot-fix — open the in-page listing picker so the user can
    // pick which of their listings to apply the add-on to. The actual Stripe
    // checkout for boost/premium needs a listing_id (backend enforces ownership).
    setPickerKind(cta);
    setPickerOpen(true);
    loadMyListings();
  };

  // --- Listing-Picker (Phase 56.1 hot-fix) ---
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState(""); // "boost" | "premium"
  const [myListings, setMyListings] = useState(null); // null = loading, [] = empty, [...] = data
  const [pickerBusy, setPickerBusy] = useState("");

  const loadMyListings = async () => {
    try {
      setMyListings(null);
      const data = await api.myListings();
      const items = Array.isArray(data) ? data : (data?.items || []);
      // Filter to active listings (you can only boost/premium active inserate).
      setMyListings(items.filter((l) => l.status === "active" || !l.status));
    } catch (e) {
      setMyListings([]);
    }
  };

  const triggerAddonCheckout = async (listing) => {
    if (!listing?.id) return;
    setPickerBusy(listing.id);
    try {
      const res = pickerKind === "premium"
        ? await api.createPremiumCheckout(listing.id)
        : await api.createBoostCheckout(listing.id, "boost-7d");
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("no checkout url");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout konnte nicht gestartet werden");
      setPickerBusy("");
    }
  };

  const subscribeDealer = async (packageId, planKey) => {
    if (!user) { navigate("/login?next=/pricing"); return; }
    setBusy(planKey);
    try {
      track("payment_started", { plan: planKey, price: planKey === "dealer_basic" ? 49 : 99, recurring: true });
      const { url } = await api.createDealerSubscription(packageId);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
      setBusy("");
    }
  };

  // Build the typed plan objects from catalog data (price comes from server, not hardcoded).
  const individualPlans = useMemo(() => {
    const plans = [];
    if (freePkg) plans.push({
      id: "free",
      name: c.plans.free.name,
      tagline: c.plans.free.tagline,
      features: c.plans.free.features,
      icon: <Shield className="h-5 w-5 text-zinc-300" />,
      priceLabel: null,
      freeLabel: c.free,
      priceSuffix: "",
    });
    if (boostPkg) plans.push({
      id: "boost",
      name: c.plans.boost.name,
      tagline: c.plans.boost.tagline,
      features: c.plans.boost.features,
      icon: <Zap className="h-5 w-5 text-red-500" />,
      priceLabel: fmtCHFprice(boostPkg.price_chf),
      priceSuffix: c.once,
      twint: true,
    });
    if (premiumPkg) plans.push({
      id: "premium",
      name: c.plans.premium.name,
      tagline: c.plans.premium.tagline,
      features: c.plans.premium.features,
      icon: <Crown className="h-5 w-5 text-amber-400" />,
      priceLabel: fmtCHFprice(premiumPkg.price_chf),
      priceSuffix: c.once,
      twint: true,
    });
    return plans;
  }, [freePkg, boostPkg, premiumPkg, c]);

  const dealerPlans = useMemo(() => {
    const plans = [];
    if (dealerBasic) plans.push({
      id: "dealer_basic",
      name: c.plans.dealer_basic.name,
      tagline: c.plans.dealer_basic.tagline,
      features: c.plans.dealer_basic.features,
      icon: <Building2 className="h-5 w-5 text-zinc-300" />,
      priceLabel: fmtCHFprice(dealerBasic.price_chf),
      priceSuffix: c.perMonth,
      twint: false,
    });
    if (dealerPro) plans.push({
      id: "dealer_pro",
      name: c.plans.dealer_pro.name,
      tagline: c.plans.dealer_pro.tagline,
      features: c.plans.dealer_pro.features,
      icon: <Crown className="h-5 w-5 text-amber-400" />,
      priceLabel: fmtCHFprice(dealerPro.price_chf),
      priceSuffix: c.perMonth,
      twint: false,
    });
    return plans;
  }, [dealerBasic, dealerPro, c]);

  const isCurrentPkg = (pid) => sub?.package === pid;

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="pricing-page">
      {breadcrumbs && <JsonLd id="pricing-breadcrumbs" data={breadcrumbs} />}
      {/* Hero */}
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-500" data-testid="pricing-kicker">{c.kicker}</p>
        <h1 className="mt-3 font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="pricing-title">{c.title}</h1>
        <p className="mt-4 text-base text-zinc-400 sm:text-lg">{c.sub}</p>
      </div>

      {/* Individuals */}
      <section className="mt-16" data-testid="pricing-section-individuals">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-lg font-semibold text-white">{c.individuals}</h2>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="pricing-grid-individuals">
          {individualPlans.map((plan, idx) => {
            const highlight = plan.id === "boost";
            const badge = plan.id === "boost" ? c.mostPopular : undefined;
            const accent = plan.id === "premium" ? "amber" : "red";
            const isBusy = busy === plan.id;
            const ctaLabel = isBusy
              ? c.processing
              : plan.id === "free"
                ? (usedFree ? "Bereits genutzt" : c.activate)
                : c.select;
            const disabled = (plan.id === "free" && usedFree) || isBusy;
            const onAction = plan.id === "free" ? activateFree : () => goToSellWith(plan.id);
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                busy={disabled}
                highlight={highlight}
                badge={badge}
                accent={accent}
                onAction={onAction}
                ctaLabel={ctaLabel}
              />
            );
          })}
        </div>
      </section>

      {/* Dealers */}
      <section className="mt-20" data-testid="pricing-section-dealers">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-lg font-semibold text-white">{c.dealers}</h2>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="pricing-grid-dealers">
          {dealerPlans.map((plan) => {
            const isCurrent = isCurrentPkg(plan.id === "dealer_basic" ? "dealer_basic_monthly" : "dealer_pro_monthly");
            const highlight = plan.id === "dealer_pro";
            const badge = plan.id === "dealer_pro" ? c.bestForDealers : undefined;
            const isBusy = busy === plan.id;
            // Phase 58 — show "14-day free trial" CTA label (unless this is the current plan).
            const ctaLabel = isBusy ? c.processing : isCurrent ? c.current : (c.trialBadge || c.select);
            const disabled = isBusy || isCurrent;
            const packageId = plan.id === "dealer_basic" ? "dealer_basic_monthly" : "dealer_pro_monthly";
            // Pass a trial banner into the card so it shows above the price.
            const trialPlan = { ...plan, trialBadge: !isCurrent ? c.trialBadge : null };
            return (
              <PlanCard
                key={plan.id}
                plan={trialPlan}
                busy={disabled}
                highlight={highlight}
                badge={badge}
                accent={plan.id === "dealer_pro" ? "amber" : "red"}
                onAction={() => subscribeDealer(packageId, plan.id)}
                ctaLabel={ctaLabel}
              />
            );
          })}
        </div>
        <p className="mt-4 text-xs text-zinc-500" data-testid="dealer-payment-note">
          {c.trialNote || c.methodsDealer}
        </p>
      </section>

      {/* Trust footer */}
      <section className="mt-20 grid gap-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 p-8 md:grid-cols-3" data-testid="pricing-trust">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-violet-600/15 text-violet-300">
            <Smartphone className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display text-sm font-semibold text-white">TWINT</p>
            <p className="mt-1 text-xs text-zinc-500">Schweizer Mobile-Payment. Schnell &amp; sicher.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-800 text-zinc-300">
            <CreditCard className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display text-sm font-semibold text-white">{c.secure}</p>
            <p className="mt-1 text-xs text-zinc-500">{c.methods}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-600/15 text-red-400">
            <Shield className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display text-sm font-semibold text-white">Stripe</p>
            <p className="mt-1 text-xs text-zinc-500">PCI-DSS Level 1 zertifiziert. Keine Kartendaten auf CarHive.</p>
          </div>
        </div>
      </section>

      <p className="mt-10 text-center text-[11px] text-zinc-600" data-testid="pricing-note">{c.note}</p>
      <p className="mt-3 text-center text-sm">
        <span className="text-zinc-500">{c.needHelp}</span>{" "}
        <Link to="/contact" className="font-semibold text-red-400 hover:text-red-300" data-testid="pricing-contact-link">
          {c.contactCta}
        </Link>
      </p>

      {/* Phase 56.1 hot-fix — Listing-Picker Modal for Boost/Premium addon checkout */}
      <Dialog open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (!o) setPickerBusy(""); }}>
        <DialogContent className="border-zinc-800 bg-zinc-950 text-white sm:max-w-lg" data-testid="addon-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white">
              {pickerKind === "premium" ? (
                <><Crown className="h-5 w-5 text-amber-400" /> Premium aktivieren</>
              ) : (
                <><Zap className="h-5 w-5 text-red-500" /> Boost aktivieren</>
              )}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {pickerKind === "premium"
                ? "Wähle das Inserat, dem du Premium (30 Tage Priority Placement, CHF 19.90) hinzufügen möchtest."
                : "Wähle das Inserat, das du 7 Tage lang zuoberst zeigen möchtest (CHF 9.90)."}
            </DialogDescription>
          </DialogHeader>

          {myListings === null && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500" data-testid="addon-picker-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Inserate werden geladen…
            </div>
          )}

          {Array.isArray(myListings) && myListings.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-8 text-center" data-testid="addon-picker-empty">
              <p className="max-w-sm text-sm text-zinc-400">
                Du hast noch kein aktives Inserat. Erstelle zuerst ein Inserat — du kannst {pickerKind === "premium" ? "Premium" : "Boost"} direkt im Dashboard hinzufügen.
              </p>
              <Button
                onClick={() => { setPickerOpen(false); navigate("/sell"); }}
                className="bg-red-600 hover:bg-red-500"
                data-testid="addon-picker-cta-create"
              >
                Inserat erstellen →
              </Button>
            </div>
          )}

          {Array.isArray(myListings) && myListings.length > 0 && (
            <div className="max-h-[55vh] overflow-y-auto" data-testid="addon-picker-listings">
              <ul className="space-y-2">
                {myListings.map((l) => {
                  const isCurrentlyActive = pickerKind === "premium"
                    ? (l.premium && l.premium_until && new Date(l.premium_until) > new Date())
                    : (l.featured && l.featured_until && new Date(l.featured_until) > new Date());
                  return (
                    <li key={l.id}>
                      <button
                        type="button"
                        disabled={pickerBusy === l.id || isCurrentlyActive}
                        onClick={() => triggerAddonCheckout(l)}
                        className="group flex w-full items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-left transition hover:border-red-500/40 hover:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid={`addon-picker-listing-${l.id}`}
                      >
                        <div className="h-14 w-20 flex-shrink-0 overflow-hidden rounded bg-zinc-900">
                          {l.images?.[0] && <img src={api.fileUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{l.title}</p>
                          <p className="mt-0.5 text-xs text-zinc-500">{l.brand} · {l.year}</p>
                          {isCurrentlyActive && (
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">
                              {pickerKind === "premium" ? "Bereits Premium" : "Bereits geboostet"}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400 group-hover:text-red-300">
                          {pickerBusy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "→"}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <DialogFooter>
            <p className="text-[11px] text-zinc-500">
              Du wirst zu Stripe weitergeleitet. TWINT &amp; Karte verfügbar.
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
